import { useCallback, useMemo } from "react";
import {
  CONNECTOR_FAMILY_CODE_KKT_A4_AISI316,
  CONNECTOR_FAMILY_CODE_KKT_COLOR,
} from "../../../http/deckDesigner/types/connectorFamilies";
import type { ConnectorForSettings } from "../../../http/deckDesigner/types/connectors";
import {
  GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING,
  GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING,
} from "../../../http/deckDesigner/types/groovingTypes";
import type { SecondaryItem } from "../../../http/deckDesigner/types/secondaryItems";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import {
  SUBSTRUCTURE_MATERIAL_CODE_TIMBER,
  SUBSTRUCTURE_MATERIAL_CODE_WPC,
} from "../../../http/deckDesigner/types/substructureMaterials";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { getAsymmetricalGroovingMinimumBoardThickness } from "./boardThicknessConstants";
import { getMaximumConnectorScrewLength, getMinimumConnectorScrewLength } from "./connectorScrewLength";
import { getOptionsByItemId } from "./itemOption";
import { calculateValidSupportRange } from "./supportHeight";

// Screw connector families with their own length formulas (see connectorsAvailableForContext).
const KKT_CONNECTOR_FAMILY_CODES = new Set<string>([
  CONNECTOR_FAMILY_CODE_KKT_COLOR,
  CONNECTOR_FAMILY_CODE_KKT_A4_AISI316,
]);

type ConnectorHeightContext = Pick<
  DeckDesignerConfigurationFormValues,
  "substructure" | "boardThickness" | "support" | "secondaryItem" | "totalHeight"
>;

// Whether the connector keeps the total height reachable. substructure and boardThickness are allowed
// to be null here - calculateValidSupportRange already treats each missing piece as contributing 0 to
// the stack height, matching the server's own calculation, so this checks against whatever is
// currently known. The upper bound only applies once a support is picked - without one, its maxHeight
// is unknowable, so treating it as 0 would falsely flag any real total height as too tall.
const connectorFitsTotalHeight = (
  connector: ConnectorForSettings,
  { substructure, boardThickness, support, secondaryItem, totalHeight }: ConnectorHeightContext,
): boolean => {
  const { minimumDeckHeightMm, maximumDeckHeightMm, totalHeightMm } = calculateValidSupportRange(
    substructure,
    boardThickness,
    support,
    secondaryItem,
    connector,
    totalHeight,
  );

  return (
    totalHeightMm == null ||
    (minimumDeckHeightMm <= totalHeightMm && (support == null || totalHeightMm <= maximumDeckHeightMm))
  );
};

type ConnectorOptionsParams = Pick<
  DeckDesignerConfigurationFormValues,
  | "fasteningType"
  | "groovingType"
  | "gap"
  | "connectorFamily"
  | "connector"
  | "substructureMaterial"
  | "boardThickness"
  | "boardWidth"
  | "groovingHeight"
  | "substructure"
  | "secondaryItem"
  | "support"
  | "totalHeight"
> & {
  settings: Settings | undefined;
};

// The connector side of ConnectorsCard: which connector families, connectors and connector screws
// the current configuration offers, and why a list is empty when it is.
export const useConnectorOptions = ({
  settings,
  fasteningType,
  groovingType,
  gap,
  connectorFamily,
  connector,
  substructureMaterial,
  boardThickness,
  boardWidth,
  groovingHeight,
  substructure,
  secondaryItem,
  support,
  totalHeight,
}: ConnectorOptionsParams) => {
  const connectorFamilies = settings?.connectorFamilies;
  const connectorMappings = settings?.connectorMappings;
  const connectors = settings?.connectors;
  const screws = settings?.screws;
  const isSymmetricalGrooving = groovingType?.code === GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING;
  const isAsymmetricalGrooving = groovingType?.code === GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING;
  const substructureMaterialCode = substructureMaterial?.code;
  const fasteningTypeCode = fasteningType?.code;
  const groovingTypeCode = groovingType?.code;
  const connectorFamilyCode = connectorFamily?.code;
  const connectorFamilyCodeForSelectedConfiguration = connectorFamily?.code ?? connector?.connectorFamily.code;

  const connectorFamilyByCode = useMemo(
    () => new Map(connectorFamilies?.map((option) => [option.code, option])),
    [connectorFamilies],
  );

  const connectorMappingsForBoardContext = useMemo(
    () =>
      connectorMappings?.filter(
        (option) =>
          (!substructureMaterialCode ||
            option.substructureMaterials.some((m) => m.code === substructureMaterialCode)) &&
          (!fasteningTypeCode || option.fasteningType.code === fasteningTypeCode) &&
          (!groovingTypeCode || option.groovingType.code === groovingTypeCode) &&
          (gap == null || (option.minimumGap <= gap && option.maximumGap >= gap)),
      ),
    [connectorMappings, fasteningTypeCode, gap, groovingTypeCode, substructureMaterialCode],
  );

  const boardContextConnectorFamilyCodes = useMemo(
    () =>
      connectorMappingsForBoardContext &&
      new Set(connectorMappingsForBoardContext.map((option) => option.connectorFamily.code)),
    [connectorMappingsForBoardContext],
  );

  // The board context's mappings once every criterion is known, narrowed to the selected family.
  // undefined = settings not loaded yet, null = a criterion is still missing.
  const connectorMappingsForSelectedConfiguration = useMemo(() => {
    if (!connectorMappingsForBoardContext) {
      return undefined;
    }

    if (
      !substructureMaterialCode ||
      !fasteningTypeCode ||
      !groovingTypeCode ||
      !connectorFamilyCodeForSelectedConfiguration ||
      gap == null
    ) {
      return null;
    }

    return connectorMappingsForBoardContext.filter(
      (option) => option.connectorFamily.code === connectorFamilyCodeForSelectedConfiguration,
    );
  }, [
    connectorFamilyCodeForSelectedConfiguration,
    connectorMappingsForBoardContext,
    fasteningTypeCode,
    gap,
    groovingTypeCode,
    substructureMaterialCode,
  ]);

  // The secondary item depends on the connector family (e.g. TVM -> PROFID, SNAP -> SNAP), so it's
  // only offered once a family is selected. undefined = settings not loaded yet.
  const mappedSecondaryItem =
    connectorMappingsForSelectedConfiguration === undefined
      ? undefined
      : (connectorMappingsForSelectedConfiguration?.find((option) => !!option.secondaryItem)?.secondaryItem ?? null);

  // Connectors matching the raw mapping context (substructure/fastening/grooving/gap), independent
  // of which family is currently selected - used below to check, per family, whether it has at
  // least one connector that still fits the current total height.
  const connectorsForBoardContext = useMemo(
    () => connectors?.filter((option) => boardContextConnectorFamilyCodes?.has(option.connectorFamily.code)),
    [boardContextConnectorFamilyCodes, connectors],
  );

  // A family only qualifies once at least one of its connectors keeps the deck's total height
  // achievable with the selected support (or any support, if none is selected yet) - the
  // per-connector height check of filteredConnectors below, applied per family instead.
  const getConnectorFamilyCodesFittingHeight = useCallback(
    (candidateSecondaryItem: SecondaryItem | null) => {
      if (totalHeight == null || !connectorsForBoardContext) {
        return null;
      }

      const heightContext = {
        substructure,
        boardThickness,
        support,
        secondaryItem: candidateSecondaryItem,
        totalHeight,
      };

      return new Set(
        connectorsForBoardContext
          .filter((option) => connectorFitsTotalHeight(option, heightContext))
          .map((option) => option.connectorFamily.code),
      );
    },
    [boardThickness, connectorsForBoardContext, substructure, support, totalHeight],
  );

  const connectorFamilyCodesFittingHeight = useMemo(
    () => getConnectorFamilyCodesFittingHeight(secondaryItem),
    [getConnectorFamilyCodesFittingHeight, secondaryItem],
  );

  // Turning the secondary item on adds its thickness to the stack, which could push the selected
  // family out of the total height - block the switch instead of letting the family get cleared.
  const secondaryItemDoesNotFitHeight = useMemo(
    () =>
      !secondaryItem &&
      !!mappedSecondaryItem &&
      !!connectorFamilyCode &&
      getConnectorFamilyCodesFittingHeight(mappedSecondaryItem)?.has(connectorFamilyCode) === false,
    [connectorFamilyCode, getConnectorFamilyCodesFittingHeight, mappedSecondaryItem, secondaryItem],
  );

  // Board thickness/grooving compatibility only - height is applied as a separate, further filter
  // below so the two reasons a family can be excluded stay distinguishable for error messaging.
  const connectorFamiliesFittingBoard = useMemo(() => {
    const baseConnectorFamilies =
      boardContextConnectorFamilyCodes && connectorFamilies
        ? connectorFamilies.filter((option) => boardContextConnectorFamilyCodes.has(option.code))
        : connectorFamilies;

    return baseConnectorFamilies?.filter((option) => {
      if (option.maxBoardThickness != null && boardThickness != null && boardThickness > option.maxBoardThickness) {
        return false;
      }

      // Symmetrical grooving centers the groove by construction (groovingHeight is derived from
      // boardThickness, not chosen) - a family only qualifies if that derived height actually falls
      // within its own grooving range. Compared as a boardThickness range (not a divided-by-2,
      // rounded height) so a boardThickness/groovingThickness difference that's odd can't get
      // rounded up into satisfying a minimum it doesn't actually reach - mirrors BoardsCard's own
      // validateBoardThickness symmetric-grooving check exactly.
      if (
        isSymmetricalGrooving &&
        boardThickness != null &&
        option.groovingThickness != null &&
        option.groovingMinHeight != null &&
        option.groovingMaxHeight != null
      ) {
        const minBoardThicknessForGrooving = option.groovingMinHeight * 2 + option.groovingThickness;
        const maxBoardThicknessForGrooving = option.groovingMaxHeight * 2 + option.groovingThickness;

        if (boardThickness < minBoardThicknessForGrooving || boardThickness > maxBoardThicknessForGrooving) {
          return false;
        }
      }

      // Asymmetrical grooving isn't centered, so the board must be thick enough to hold the groove
      // (chosen as groovingHeight) plus this family's own groovingThickness, with a minimum margin
      // above it - same constant BoardsCard's own validateBoardThickness uses, so the two can't drift.
      if (
        isAsymmetricalGrooving &&
        boardThickness != null &&
        groovingHeight != null &&
        option.groovingThickness != null &&
        boardThickness < getAsymmetricalGroovingMinimumBoardThickness(groovingHeight, option.groovingThickness)
      ) {
        return false;
      }

      return true;
    });
  }, [
    boardContextConnectorFamilyCodes,
    boardThickness,
    connectorFamilies,
    groovingHeight,
    isAsymmetricalGrooving,
    isSymmetricalGrooving,
  ]);

  const filteredConnectorFamilies = useMemo(
    () =>
      connectorFamilyCodesFittingHeight
        ? connectorFamiliesFittingBoard?.filter((option) => connectorFamilyCodesFittingHeight.has(option.code))
        : connectorFamiliesFittingBoard,
    [connectorFamiliesFittingBoard, connectorFamilyCodesFittingHeight],
  );

  const filteredConnectorFamilyCodes = useMemo(
    () => filteredConnectorFamilies && new Set(filteredConnectorFamilies.map((option) => option.code)),
    [filteredConnectorFamilies],
  );

  // The three, mutually exclusive, reasons filteredConnectorFamilies can end up empty, so the field
  // can tell the user which part of the configuration to change instead of silently showing nothing.
  const hasNoConnectorFamilyMapping =
    !!connectorMappingsForBoardContext && connectorMappingsForBoardContext.length === 0;
  const hasNoConnectorFamilyFittingBoard =
    !!connectorMappingsForBoardContext?.length && connectorFamiliesFittingBoard?.length === 0;
  // Board-fitting families exist, but none of them survive the height filter - the height, not the
  // board thickness/grooving, is what the user needs to change.
  const hasNoConnectorFamilyFittingHeight =
    !!connectorFamiliesFittingBoard?.length && filteredConnectorFamilies?.length === 0;

  // Keeps the currently selected family in the option list even once it's filtered out (e.g. the
  // board thickness grew past its maxBoardThickness) - otherwise the select would render as empty
  // instead of showing the now-invalid selection with its error.
  const selectedConnectorFamily = useMemo(() => {
    const selectedConnectorFamilyCode = connectorFamily?.code ?? connector?.connectorFamily.code;

    if (!selectedConnectorFamilyCode) {
      return null;
    }

    return (
      connectorFamilyByCode.get(selectedConnectorFamilyCode) ?? connector?.connectorFamily ?? connectorFamily ?? null
    );
  }, [connector, connectorFamily, connectorFamilyByCode]);

  const selectedConnectorFamilyCode = selectedConnectorFamily?.code;

  const selectedConnectorFamilyIsAvailable =
    !connectorFamilyCode ||
    !boardContextConnectorFamilyCodes ||
    boardContextConnectorFamilyCodes.has(connectorFamilyCode);

  // filteredConnectorFamilies additionally excludes families that no longer fit the board (max
  // thickness, symmetrical/asymmetrical grooving range) - selectedConnectorFamilyIsAvailable above
  // only tracks connector-mapping availability, so this catches the board-constraint case too.
  // Checked against the height-aware filteredConnectorFamilies (not just board thickness/grooving) so
  // a family that's become incompatible with the current total height also gets cleared - totalHeight
  // is debounced upstream, so this only reacts once the value has actually settled.
  const selectedConnectorFamilyFitsBoardConstraints =
    !connectorFamilyCode || !filteredConnectorFamilyCodes || filteredConnectorFamilyCodes.has(connectorFamilyCode);

  // The families whose connectors are offered: the selected one while it's still mapped, otherwise
  // every family left in the picker. filteredConnectorFamilies additionally excludes families that
  // don't fit the board (max thickness, symmetrical/asymmetrical grooving range) - when no family is
  // selected yet, the connectors offered below must be restricted the same way, otherwise a connector
  // from an unfit family could still show up as selectable even though its family no longer appears
  // in the family picker. filteredConnectorFamilies only holds board context families already.
  const connectorFamilyCodesForContext = useMemo(() => {
    if (selectedConnectorFamilyCode && selectedConnectorFamilyIsAvailable) {
      return new Set(
        boardContextConnectorFamilyCodes?.has(selectedConnectorFamilyCode) ? [selectedConnectorFamilyCode] : [],
      );
    }

    return boardContextConnectorFamilyCodes && (filteredConnectorFamilyCodes ?? boardContextConnectorFamilyCodes);
  }, [
    boardContextConnectorFamilyCodes,
    filteredConnectorFamilyCodes,
    selectedConnectorFamilyCode,
    selectedConnectorFamilyIsAvailable,
  ]);

  const maxScrewConnectorLength = useMemo(() => {
    if (boardThickness == null || substructure?.height == null) {
      return undefined;
    }

    return boardThickness + (secondaryItem?.thickness ?? 0) + substructure.height;
  }, [boardThickness, secondaryItem?.thickness, substructure?.height]);

  const connectorsAvailableForContext = useMemo(
    () =>
      connectors?.filter((option) => {
        if (!connectorFamilyCodesForContext?.has(option.connectorFamily.code)) {
          return false;
        }

        if (groovingHeight != null && option.minGroovingHeight != null && option.minGroovingHeight > groovingHeight) {
          return false;
        }

        if (option.minBoardWidth != null && boardWidth != null && boardWidth < option.minBoardWidth) {
          return false;
        }

        if (!option.screw) {
          return !option.connectorFamily.isScrew;
        }

        // KKT families have their own length formulas (connectorScrewLength.ts, mirrored by the server);
        // the others (KKA) keep the generic rule: 4 diameters into the substructure below the board,
        // without poking through its bottom face.
        if (KKT_CONNECTOR_FAMILY_CODES.has(option.connectorFamily.code)) {
          const screwLengthInput = {
            connectorFamilyCode: option.connectorFamily.code,
            substructureMaterial,
            boardThickness,
            groovingHeight,
            groovingThickness: option.connectorFamily.groovingThickness,
            connectorMinGroovingHeight: option.minGroovingHeight,
            substructure,
            secondaryItem,
            screw: option.screw,
          };
          const minimumLength = getMinimumConnectorScrewLength(screwLengthInput);
          const maximumLength = getMaximumConnectorScrewLength(screwLengthInput);

          return (
            (minimumLength == null || option.screw.length >= minimumLength) &&
            (maximumLength == null || option.screw.length <= maximumLength)
          );
        }

        if (boardThickness != null && option.screw.length < boardThickness + 4 * option.screw.screwDiameter) {
          return false;
        }

        return maxScrewConnectorLength == null || option.screw.length <= maxScrewConnectorLength;
      }),
    [
      boardThickness,
      boardWidth,
      connectorFamilyCodesForContext,
      connectors,
      groovingHeight,
      maxScrewConnectorLength,
      secondaryItem,
      substructure,
      substructureMaterial,
    ],
  );

  // Connectors the support height checks run against: the selected one or, before one is picked,
  // one per distinct thickness among those available here - so a support is offered when it fits
  // with at least one of them, instead of assuming a 0 mm connector (which rejected supports that
  // fit once the connector's thickness is in the stack). [null] when there's no connector at all.
  const supportCheckConnectors = useMemo((): (ConnectorForSettings | null)[] => {
    if (connector) {
      return [connector];
    }

    const connectorByThickness = new Map<number, ConnectorForSettings>();
    connectorsAvailableForContext?.forEach((option) => {
      if (!connectorByThickness.has(option.thickness)) {
        connectorByThickness.set(option.thickness, option);
      }
    });

    return connectorByThickness.size
      ? [...connectorByThickness.values()].sort((a, b) => a.thickness - b.thickness)
      : [null];
  }, [connector, connectorsAvailableForContext]);

  const filteredConnectors = useMemo(() => {
    if (!connectorsAvailableForContext || totalHeight == null) {
      return connectorsAvailableForContext;
    }

    const heightContext = { substructure, boardThickness, support, secondaryItem, totalHeight };

    return connectorsAvailableForContext.filter((option) => connectorFitsTotalHeight(option, heightContext));
  }, [boardThickness, connectorsAvailableForContext, secondaryItem, substructure, support, totalHeight]);

  const filteredConnectorById = useMemo(() => getOptionsByItemId(filteredConnectors), [filteredConnectors]);

  const firstSelectableScrewConnector = useMemo(() => {
    if (selectedConnectorFamily?.isScrew !== true || maxScrewConnectorLength == null) {
      return undefined;
    }

    return filteredConnectors?.find((option) => option.connectorFamily.isScrew === true && option.screw != null);
  }, [filteredConnectors, maxScrewConnectorLength, selectedConnectorFamily?.isScrew]);

  const singleSelectableConnector = filteredConnectors?.length === 1 ? filteredConnectors[0] : undefined;

  // Height (rather than mapping/board) is why no connector is selectable: the selected family
  // survives the height filter, but none of its individual connectors do.
  const hasConnectorHeightError =
    !!connectorsAvailableForContext?.length && !!filteredConnectors && filteredConnectors.length === 0;

  const hasNoCompatibleConnector = !!filteredConnectorFamilies?.length && connectorsAvailableForContext?.length === 0;

  const selectedConnectorIsScrew = !!connector?.screw || connector?.connectorFamily.isScrew === true;
  const shouldShowConnectorScrew = !selectedConnectorIsScrew && selectedConnectorFamily?.isScrew !== true;
  const shouldShowConnectorBoardScrew =
    shouldShowConnectorScrew && selectedConnectorFamily?.requiresBoardScrew === true;

  const connectorSubstructureScrewFamiliesForMaterial = useMemo(() => {
    const screwFamilies =
      selectedConnectorFamily?.screwFamilies ?? connectorFamilies?.flatMap((family) => family.screwFamilies);

    if (!screwFamilies) {
      return undefined;
    }

    if (!substructureMaterialCode) {
      return screwFamilies;
    }

    return screwFamilies.filter((family) =>
      family.substructureMaterials.some((m) => m.code === substructureMaterialCode),
    );
  }, [connectorFamilies, selectedConnectorFamily, substructureMaterialCode]);

  const filteredConnectorScrews = useMemo(() => {
    if (!connectorSubstructureScrewFamiliesForMaterial) {
      return screws;
    }

    const screwFamilyIds = new Set(connectorSubstructureScrewFamiliesForMaterial.map((family) => family.id));

    return screws?.filter((screw) => {
      if (!screwFamilyIds.has(screw.screwFamily.id)) {
        return false;
      }

      const screwLengthInput = {
        connectorFamilyCode: selectedConnectorFamily?.code,
        substructureMaterial,
        boardThickness,
        groovingHeight,
        groovingThickness: selectedConnectorFamily?.groovingThickness,
        connectorMinGroovingHeight: connector?.minGroovingHeight,
        substructure,
        secondaryItem,
        screw,
      };

      const minimumConnectorScrewLength = getMinimumConnectorScrewLength(screwLengthInput);

      if (minimumConnectorScrewLength != null && screw.length < minimumConnectorScrewLength) {
        return false;
      }

      const maximumConnectorScrewLength = getMaximumConnectorScrewLength(screwLengthInput);

      return maximumConnectorScrewLength == null || screw.length <= maximumConnectorScrewLength;
    });
  }, [
    boardThickness,
    connector?.minGroovingHeight,
    connectorSubstructureScrewFamiliesForMaterial,
    groovingHeight,
    screws,
    secondaryItem,
    selectedConnectorFamily,
    substructure,
    substructureMaterial,
  ]);

  const filteredConnectorScrewById = useMemo(
    () => getOptionsByItemId(filteredConnectorScrews),
    [filteredConnectorScrews],
  );

  const connectorBoardScrewFamilies = useMemo(() => {
    if (!shouldShowConnectorBoardScrew) {
      return [];
    }

    return (
      selectedConnectorFamily?.screwFamilies.filter((family) =>
        family.substructureMaterials.some(
          (m) => m.code === SUBSTRUCTURE_MATERIAL_CODE_TIMBER || m.code === SUBSTRUCTURE_MATERIAL_CODE_WPC,
        ),
      ) ?? []
    );
  }, [selectedConnectorFamily?.screwFamilies, shouldShowConnectorBoardScrew]);

  const filteredConnectorBoardScrews = useMemo(() => {
    if (!shouldShowConnectorBoardScrew || boardThickness == null) {
      return [];
    }

    if (!screws) {
      return undefined;
    }

    const screwFamilyIds = new Set(connectorBoardScrewFamilies.map((family) => family.id));
    const minimumConnectorBoardScrewLength = boardThickness / 3;

    return screws.filter(
      (screw) =>
        screwFamilyIds.has(screw.screwFamily.id) &&
        screw.length >= minimumConnectorBoardScrewLength &&
        screw.length <= boardThickness,
    );
  }, [boardThickness, connectorBoardScrewFamilies, screws, shouldShowConnectorBoardScrew]);

  const filteredConnectorBoardScrewById = useMemo(
    () => getOptionsByItemId(filteredConnectorBoardScrews),
    [filteredConnectorBoardScrews],
  );

  const hasNoCompatibleConnectorSubstructureScrew =
    shouldShowConnectorScrew && !!connector && !!filteredConnectorScrews && filteredConnectorScrews.length === 0;

  const hasNoCompatibleConnectorBoardScrew =
    shouldShowConnectorBoardScrew && !!filteredConnectorBoardScrews && filteredConnectorBoardScrews.length === 0;

  return {
    isSymmetricalGrooving,
    isAsymmetricalGrooving,
    connectorFamilyByCode,
    connectorMappingsForBoardContext,
    connectorMappingsForSelectedConfiguration,
    mappedSecondaryItem,
    secondaryItemDoesNotFitHeight,
    filteredConnectorFamilies,
    hasNoConnectorFamilyMapping,
    hasNoConnectorFamilyFittingBoard,
    hasNoConnectorFamilyFittingHeight,
    selectedConnectorFamily,
    selectedConnectorFamilyIsAvailable,
    selectedConnectorFamilyFitsBoardConstraints,
    maxScrewConnectorLength,
    supportCheckConnectors,
    filteredConnectors,
    filteredConnectorById,
    firstSelectableScrewConnector,
    singleSelectableConnector,
    hasConnectorHeightError,
    hasNoCompatibleConnector,
    shouldShowConnectorScrew,
    shouldShowConnectorBoardScrew,
    filteredConnectorScrews,
    filteredConnectorScrewById,
    filteredConnectorBoardScrews,
    filteredConnectorBoardScrewById,
    hasNoCompatibleConnectorSubstructureScrew,
    hasNoCompatibleConnectorBoardScrew,
  };
};

export type ConnectorOptions = ReturnType<typeof useConnectorOptions>;
